<template>
  <div>
    <template v-if="variant === 'application'">
      <h2 class="text-h4 mb-16">Activity-inspired care</h2>
    </template>

    <p class="text-grey-body mb-16">
      {{ adventureDescription }}
    </p>

    <!-- Grid container for application variant, single column for profile -->
    <div v-if="variant === 'application'" class="adventures-grid">
      <ComplexCard
        v-for="adventure in displayedAdventures"
        :key="adventure.id"
        :class="'w-full'"
        :card-class="cardClassProp"
        variant="active"
        :elevation="1"
        :title="adventure.title"
        :header-image-url="adventure.adventureCategoryImage.url"
        :first-line-text="firstLineText(adventure)"
        :details-url="detailsUrl(adventure)"
      />
    </div>

    <!-- Single column layout for profile variant -->
    <template v-else>
      <ComplexCard
        v-for="adventure in displayedAdventures"
        :key="adventure.id"
        class="w-full mb-16"
        :card-class="cardClassProp"
        variant="active"
        :elevation="1"
        :title="adventure.title"
        :header-image-url="adventure.adventureCategoryImage.url"
        :first-line-text="firstLineText(adventure)"
        :details-url="detailsUrl(adventure)"
      />
    </template>

    <div v-if="adventuresDisplayedCount < adventures.length" class="mt-16">
      <Button
        value="View More"
        variant="outlined"
        :size="variant === 'application' ? 'small' : 'medium'"
        :class="variant === 'application' ? '' : 'w-full'"
        @click="loadMoreAdventures"
      />
    </div>
  </div>
</template>

<script lang="ts">
import Button from '../button/Button.vue';
import ComplexCard from '../complex_card/ComplexCard.vue';

interface Adventure {
  id: number;
  title: string;
  adventureCategoryName: string;
  adventureCategoryImage: { url: string };
  ageRangeText: string;
  status: string;
}

interface SitterData {
  location?: {
    stateabbr: string;
    cityname: string;
  };
}

export default {
  name: 'AdventuresList',
  components: {
    Button,
    ComplexCard,
  },
  props: {
    adventures: {
      type: Array as () => Adventure[],
      required: true,
    },
    sitter: {
      type: Object as () => SitterData,
      required: true,
    },
    variant: {
      type: String as () => 'application' | 'profile',
      required: true,
      validator: (value: string) => ['application', 'profile'].includes(value),
    },
    displayName: {
      type: String,
      required: true,
    },
    returnPath: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      adventuresDisplayedCount: 2,
    };
  },
  computed: {
    adventureDescription(): string {
      return `${this.displayName} offers ${this.adventureText[0].toLowerCase()} ${this.adventureText[1]}`;
    },
    adventureText(): string[] {
      if (this.adventures.length === 1) {
        return [`${this.adventures[0].adventureCategoryName}`, 'Adventure'];
      }
      return [`${this.adventures.length}`, 'Adventures'];
    },
    displayedAdventures(): Adventure[] {
      return this.adventures.slice(0, this.adventuresDisplayedCount);
    },
    cardClassProp(): string {
      const baseClasses = 'border-1 border-solid border-grey-divider-subdued hover:box-shadow-sm';
      if (this.variant === 'profile') {
        return `${baseClasses} adventures-profile-card`;
      }
      if (this.variant === 'application') {
        return `${baseClasses} adventures-application-card`;
      }
      return baseClasses;
    },
  },
  methods: {
    loadMoreAdventures(): void {
      this.adventuresDisplayedCount += 2;
    },
    firstLineText(adventure: Adventure): string {
      return `Ages ${adventure.ageRangeText} • ${adventure.adventureCategoryName}`;
    },
    detailsUrl(adventure: Adventure): string {
      const sitterLocation = this.sitter as SitterData;
      const { stateabbr = '', cityname = '' } = sitterLocation.location || {};
      let link = `/adventures/details/${stateabbr}/${cityname}/${adventure.id}`;
      if (this.returnPath) {
        link += `?return_path=${encodeURIComponent(this.returnPath)}`;
      }
      return link;
    },
  },
};
</script>

<style scoped>
/* Responsive grid for application variant - auto-fits columns with 300px minimum */
.adventures-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  max-width: 100%;
  margin-top: 4px;
}

/* Override ComplexCard's default max-width for profile variant */
.adventures-profile-card :deep(.complex-card) {
  max-width: none !important;
}

/* Ensure application variant cards fill grid space properly */
.adventures-application-card :deep(.complex-card) {
  width: 100% !important;
  max-width: none !important;
}

.adventures-application-card :deep(.card) {
  width: 100% !important;
  max-width: none !important;
}
</style>
